/// <reference path="./arrow.d.ts" />
import { PageGen } from '../pdf';
import { PdfPen, PdfBrush, PdfRect, PdfPoint } from '../pdf';

/**
 * Draw an up-pointing arrow polygon.
 */
PageGen.prototype.drawArrowUp = function (
  rect: PdfRect,
  brush: PdfBrush,
  pen: PdfPen | null = null
): void {
  const d = rect.width * 0.3;
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;

  const points: PdfPoint[] = [
    new PdfPoint(cx, rect.top),
    new PdfPoint(rect.right, cy),
    new PdfPoint(rect.right - d, cy),
    new PdfPoint(rect.right - d, rect.bottom),
    new PdfPoint(rect.left + d, rect.bottom),
    new PdfPoint(rect.left + d, cy),
    new PdfPoint(rect.left, cy),
  ];

  this.drawPolygon(pen, brush, points);
};

/**
 * Draw a down-pointing arrow polygon.
 */
PageGen.prototype.drawArrowDown = function (
  rect: PdfRect,
  brush: PdfBrush,
  pen: PdfPen | null = null
): void {
  const d = rect.width * 0.3;
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;

  const points: PdfPoint[] = [
    new PdfPoint(cx, rect.bottom),
    new PdfPoint(rect.right, cy),
    new PdfPoint(rect.right - d, cy),
    new PdfPoint(rect.right - d, rect.top),
    new PdfPoint(rect.left + d, rect.top),
    new PdfPoint(rect.left + d, cy),
    new PdfPoint(rect.left, cy),
  ];

  this.drawPolygon(pen, brush, points);
};

/**
 * Draw a left-pointing arrow polygon.
 */
PageGen.prototype.drawArrowLeft = function (
  rect: PdfRect,
  brush: PdfBrush,
  pen: PdfPen | null = null
): void {
  const d = rect.height * 0.3;
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;

  const points: PdfPoint[] = [
    new PdfPoint(rect.left, cy),
    new PdfPoint(cx, rect.bottom),
    new PdfPoint(cx, rect.bottom - d),
    new PdfPoint(rect.right, rect.bottom - d),
    new PdfPoint(rect.right, rect.top + d),
    new PdfPoint(cx, rect.top + d),
    new PdfPoint(cx, rect.top),
  ];

  this.drawPolygon(pen, brush, points);
};

/**
 * Draw a right-pointing arrow polygon.
 */
PageGen.prototype.drawArrowRight = function (
  rect: PdfRect,
  brush: PdfBrush,
  pen: PdfPen | null = null
): void {
  const d = rect.height * 0.3;
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;

  const points: PdfPoint[] = [
    new PdfPoint(rect.right, cy),
    new PdfPoint(cx, rect.bottom),
    new PdfPoint(cx, rect.bottom - d),
    new PdfPoint(rect.left, rect.bottom - d),
    new PdfPoint(rect.left, rect.top + d),
    new PdfPoint(cx, rect.top + d),
    new PdfPoint(cx, rect.top),
  ];

  this.drawPolygon(pen, brush, points);
};

/**
 * Draw a line arrow with arrowhead.
 */
PageGen.prototype.drawArrow = function (
  pen: PdfPen,
  p1: PdfPoint,
  p2: PdfPoint,
  arrowHeadLength = 10,
  arrowHeadAngleDegrees = 20
): void {
  // draw the main line
  this.drawLine(pen, p1, p2);

  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  const lineLength = Math.sqrt(dx * dx + dy * dy);

  if (lineLength === 0) return;

  const ux = dx / lineLength;
  const uy = dy / lineLength;

  const arrowAngle = (arrowHeadAngleDegrees * Math.PI) / 180;

  function rotateVector(vx: number, vy: number, angle: number): [number, number] {
    const cosA = Math.cos(angle);
    const sinA = Math.sin(angle);
    return [vx * cosA - vy * sinA, vx * sinA + vy * cosA];
  }

  const [rx1, ry1] = rotateVector(-ux, -uy, arrowAngle);
  const [rx2, ry2] = rotateVector(-ux, -uy, -arrowAngle);

  const arrowPoint1 = new PdfPoint(p2.x + rx1 * arrowHeadLength, p2.y + ry1 * arrowHeadLength);
  const arrowPoint2 = new PdfPoint(p2.x + rx2 * arrowHeadLength, p2.y + ry2 * arrowHeadLength);

  this.drawLine(pen, p2, arrowPoint1);
  this.drawLine(pen, p2, arrowPoint2);
};
